import type { CategorieNdf, ChampsExtraits } from '../types/ndf';
import { champsVides, type ChampsDepense } from '../components/ndf/FormulaireDepense';

/**
 * Traduit ce que l'IA a lu en valeurs de formulaire (specs §C2.3, Lot 3).
 *
 * Fonction PURE, sans requête ni état : c'est ce qui permet de la vérifier cas
 * par cas — un ticket sans HT, un hôtel qui porte de la TVA, un abonnement.
 *
 * Deux champs ne sont JAMAIS pré-remplis, quoi que l'IA ait lu : « demander le
 * remboursement » et « refacturable à un client » (C1 champs 11 et 12). Ils
 * engagent le salarié et l'entreprise ; c'est à lui de les cocher.
 */

export interface Prefill {
  champs: ChampsDepense;
  /** Ce qu'il faut DIRE au salarié sur ce qui a été décidé pour lui. */
  messages: string[];
  /** Conservé sur la dépense pour alimenter les contrôles côté serveur. */
  lecture: {
    commercant: string | null;
    date_extraite: string | null;
    type_document: string | null;
    peage_heure: string | null;
    peage_entree: string | null;
    peage_sortie: string | null;
  };
}

export function prefillDepuisExtraction(
  extrait: ChampsExtraits,
  categories: CategorieNdf[],
): Prefill {
  const champs = champsVides();
  const messages: string[] = [];

  const categorie = categories.find((c) => c.code === extrait.categorie_code && !c.parent_id);
  const sousCategorie = categories.find(
    (c) => c.code === extrait.sous_categorie_code && c.parent_id === categorie?.id,
  );

  if (extrait.date) champs.date_depense = extrait.date;
  if (categorie) champs.categorie_id = categorie.id;
  if (sousCategorie) champs.sous_categorie_id = sousCategorie.id;
  if (extrait.description) champs.description = extrait.description;
  // À défaut de libellé, l'enseigne dit déjà de quoi il s'agit.
  else if (extrait.commercant) champs.description = extrait.commercant;

  // R-CAT-02 : sur ces catégories la TVA n'est pas récupérable. Elle est forcée
  // à zéro MÊME si le justificatif en porte — et on explique pourquoi, sinon le
  // salarié croit à une erreur de lecture et la ressaisit.
  const tvaInterdite = categorie?.regle_tva === 'forcee_zero';
  const tvaLue = extrait.montant_tva ?? 0;
  champs.montant_tva = tvaInterdite ? 0 : tvaLue;

  const ht = extrait.montant_ht ?? null;
  const ttc = extrait.montant_ttc ?? null;

  // Devise étrangère : les montants lus ne sont PAS des euros. Les poser tels
  // quels dans le formulaire ferait déclarer 50 € pour 50 CHF. On les range
  // donc dans le bloc de conversion, qui produira l'équivalent en euros.
  const enDevise = Boolean(extrait.devise && extrait.devise !== 'EUR');
  if (enDevise) {
    champs.devise = extrait.devise;
    champs.montant_original = ttc ?? ht;
    champs.montant_tva = 0;
    messages.push(
      `Ce justificatif est libellé en ${extrait.devise}. Le montant est converti au taux du jour : corrigez-le s’il diffère de votre relevé bancaire.`,
    );
    return { champs, messages, lecture: lectureDe(extrait) };
  }

  if (tvaInterdite) {
    // La TVA non récupérable n'est pas une remise : elle reste À LA CHARGE de
    // l'entreprise. Le montant pris en charge est donc le TTC, reporté en HT.
    // Reprendre le HT du justificatif ferait perdre la TVA — 104,20 € d'hôtel
    // remboursés 94,73 €, et un salarié qui paie la différence de sa poche.
    const charge = ttc ?? (ht !== null ? Math.round((ht + tvaLue) * 100) / 100 : null);
    if (charge !== null) champs.prix_unitaire_ht = charge;
    if (tvaLue > 0) {
      messages.push(
        `La TVA n’est pas récupérable sur cette catégorie : le montant TTC (${(charge ?? 0).toFixed(2)} €) est pris en charge en totalité, TVA comprise.`,
      );
    }
  } else if (ht !== null) {
    champs.prix_unitaire_ht = ht;
  } else if (ttc !== null) {
    // Beaucoup de tickets ne portent que le TTC : on reconstitue le HT plutôt
    // que de laisser un zéro, qui obligerait à tout ressaisir.
    champs.prix_unitaire_ht = Math.round((ttc - champs.montant_tva) * 100) / 100;
    if (tvaLue === 0) {
      messages.push(
        'Seul le montant TTC était lisible : vérifiez la répartition entre hors taxes et TVA.',
      );
    }
  }

  // R-DEP-10 : un abonnement mensuel n'est pris en charge qu'à 50 %. Le serveur
  // l'impose de toute façon ; l'afficher évite que le salarié croie à une
  // erreur en voyant son TTC divisé après enregistrement.
  if (categorie?.est_abonnement) {
    champs.quantite = 0.5;
    messages.push(
      'Un abonnement mensuel est pris en charge à 50 % : la quantité est fixée à 0,5.',
    );
  }

  if (!categorie) {
    messages.push('La catégorie n’a pas pu être déterminée : choisissez-la dans la liste.');
  }

  return { champs, messages, lecture: lectureDe(extrait) };
}

function lectureDe(extrait: ChampsExtraits): Prefill['lecture'] {
  return {
    commercant: extrait.commercant ?? null,
    date_extraite: extrait.date ?? null,
    type_document: extrait.type_document ?? null,
    peage_heure: extrait.peage_heure ?? null,
    peage_entree: extrait.peage_entree ?? null,
    peage_sortie: extrait.peage_sortie ?? null,
  };
}
